import { useEffect, useState, useMemo } from 'react';
import { auth, db } from '../firebase';
import { useContact } from '../contexts/ContactContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import TradeOfferForm from '../components/TradeOfferForm';
import { getInitiatorPedalIds } from '../utils/tradeUtils';
import UserGroupCard from '../components/UserGroupCard';
import { groupMatchesByUser } from '../utils/groupMatchesByUser';
import { useMatchFilters } from '../hooks/useMatchFilters';
import MatchFilterBar from '../components/MatchFilterBar';
import MatchesMobileChromeHeader, { MATCH_VIEW_TABS } from '../components/MatchesMobileChromeHeader';
import { useRouteSwipeViews } from '../hooks/useSwipeViews';
import {
  collection,
  onSnapshot,
  doc,
  getDocs,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  query,
  where
} from 'firebase/firestore';

const SavedMatches = () => {
  const { openContact } = useContact();
  const navigate = useNavigate();
  const location = useLocation();

  // Native-only swipe + slide across the Matches views (All / Saved / Hidden).
  const { swipeProps, viewClass } = useRouteSwipeViews(MATCH_VIEW_TABS, location.pathname, { liveDrag: true });
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showOfferForm, setShowOfferForm] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [hiddenMatchUsers, setHiddenMatchUsers] = useState([]);

  // Extracted filter system — owns filter state, available-options derivation,
  // apply-filters pipeline, bottom-sheet open/close, activeFilterCount, etc.
  // No storageKey: this page doesn't persist scroll/filter snapshots.
  const filters = useMatchFilters({ matches });
  const { filteredMatches, activeFilterCount } = filters;

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);


// Show/hide "Back to Top" button based on scroll position
useEffect(() => {
  const handleScroll = () => {
    if (window.scrollY > 300) {
      setShowBackToTop(true);
    } else {
      setShowBackToTop(false);
    }
  };

  window.addEventListener('scroll', handleScroll);
  return () => window.removeEventListener('scroll', handleScroll);
}, []);

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};



  const getTradeBadge = (count) => {
  if (count === undefined || count === null || isNaN(count)) return 0;
  return count; // Show exact number of completed trades, including 0
};

  const getMembershipBadge = (membershipTier) => {
    switch (membershipTier) {
      case 'premium_basic':
        return (
          <div className="flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
            <span className="bg-blue-600 text-white px-1.5 py-0.5 rounded font-bold mr-1" style={{ fontSize: '11px' }}>9V</span>
            Member
          </div>
        );
      case 'premium_plus':
        return (
          <div className="flex items-center bg-purple-100 text-purple-800 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
            <span className="bg-purple-600 text-white px-1.5 py-0.5 rounded font-bold mr-1" style={{ fontSize: '11px' }}>18V</span>
            Member
          </div>
        );
      default:
        return (
          <div className="flex items-center bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
            Free Member
          </div>
        );
    }
  };

  const getMembershipProfileBadge = (membershipTier) => {
    switch (membershipTier) {
      case 'premium_basic':
        return (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              left: '-4px',
              backgroundColor: '#2563eb',
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '4px',
              width: '20px',
              height: '12px',
              fontSize: '0.6rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #fff',
              boxShadow: '0 0 2px rgba(0,0,0,0.3)',
            }}
          >
            9V
          </div>
        );
      case 'premium_plus':
        return (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              left: '-4px',
              backgroundColor: '#7c3aed',
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '4px',
              width: '20px',
              height: '12px',
              fontSize: '0.6rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #fff',
              boxShadow: '0 0 2px rgba(0,0,0,0.3)',
            }}
          >
            18V
          </div>
        );
      default:
        return null;
    }
  };

  // Per-match unsave handler kept for reference. The main SavedMatches page no
  // longer wires this to any button after the group-card cutover — the
  // group-level handlers (handleGroupUnsave / handleGroupHide below) replace
  // it on this surface. Will be removed once the legacy savedMatches array is
  // fully retired.
  // eslint-disable-next-line no-unused-vars
  const unsaveMatch = async (match) => {
    const user = auth.currentUser;
    if (!user) return;

    const matchId = `${match.myPedal.id}_${match.matchedPedal.id}`;
    const userRef = doc(db, 'users', user.uid);

    try {
      await updateDoc(userRef, {
        savedMatches: arrayRemove(matchId)
      });

      console.log('Match removed from savedMatches array.');
    } catch (error) {
      console.error('Error removing from savedMatches array:', error);
    }
  };

  // ── Group-level unsave / hide handlers ──────────────────────────────────
  // Unsave removes the counterparty from savedMatchUsers AND removes all the
  // group's matchIds from the legacy savedMatches array, keeping both fields
  // in sync so older surfaces still reading the legacy array stay consistent.
  const handleGroupUnsave = async (group) => {
    const user = auth.currentUser;
    if (!user) return;
    const userRef = doc(db, 'users', user.uid);
    try {
      await updateDoc(userRef, {
        savedMatchUsers: arrayRemove(group.counterpartyUid),
        savedMatches: arrayRemove(...group.allMatchIds)
      });
    } catch (err) {
      console.error('Group unsave failed:', err);
    }
  };

  // Hide on a saved card snapshots the current matchIds with that
  // counterparty into hiddenMatchUsers AND removes the saved state — so the
  // card disappears from /saved-matches immediately and won't re-appear there
  // even if the hide is later removed (auto-unhide on the main Matches page
  // or manual unhide). Per product direction: "if someone clicks hide, it
  // should only appear in the hidden matches."
  const handleGroupHide = async (group, currentHiddenMatchUsers) => {
    const user = auth.currentUser;
    if (!user) return;
    const alreadyHidden = currentHiddenMatchUsers.some(
      h => h.counterpartyUid === group.counterpartyUid
    );
    const newEntry = {
      counterpartyUid: group.counterpartyUid,
      hiddenMatchIds: [...group.allMatchIds].sort()
    };
    const userRef = doc(db, 'users', user.uid);
    try {
      await updateDoc(userRef, {
        hiddenMatchUsers: alreadyHidden
          ? currentHiddenMatchUsers
          : [...currentHiddenMatchUsers, newEntry],
        hiddenMatches: arrayUnion(...group.allMatchIds),
        savedMatchUsers: arrayRemove(group.counterpartyUid),
        savedMatches: arrayRemove(...group.allMatchIds)
      });
    } catch (err) {
      console.error('Group hide failed:', err);
    }
  };

  // Function to clean up orphaned saved matches for current user. Receives a
  // prebuilt id→listing map (only the pedals referenced by these matches) so we
  // don't pull the entire listings collection here.
  const cleanupOrphanedMatches = async (savedMatchIds, listingsById) => {
    const user = auth.currentUser;
    if (!user || !savedMatchIds.length) return;

    const orphanedIds = [];
    
    for (const matchId of savedMatchIds) {
      try {
        const [myId, theirId] = matchId.split('_');
        const myPedal = listingsById.get(myId);
        const matchedPedal = listingsById.get(theirId);
        
        // If either pedal doesn't exist, is inactive, in trade, or needs reactivation, consider it orphaned
        if (!myPedal || !matchedPedal || 
            myPedal.active === false || matchedPedal.active === false ||
            myPedal.inTrade === true || matchedPedal.inTrade === true ||
            myPedal.needsReactivation === true || matchedPedal.needsReactivation === true) {
          orphanedIds.push(matchId);
        }
      } catch (error) {
        console.error(`Error checking match ${matchId}:`, error);
        orphanedIds.push(matchId); // Treat errors as orphaned
      }
    }

    if (orphanedIds.length > 0) {
      console.log(`🧹 Cleaning up ${orphanedIds.length} orphaned saved matches:`, orphanedIds);
      
      const userRef = doc(db, 'users', user.uid);
      const validIds = savedMatchIds.filter(id => !orphanedIds.includes(id));
      
      try {
        await updateDoc(userRef, {
          savedMatches: validIds
        });
        console.log('✅ Orphaned saved matches cleaned up');
      } catch (error) {
        console.error('❌ Error cleaning up orphaned matches:', error);
      }
    }
  };


  useEffect(() => {
    if (!currentUser) {
      setMatchesLoading(false);
      return;
    }

    setMatchesLoading(true);

    // Storm dedupe (2026-09-25): this doc listener runs the heaviest read
    // pipeline in the client (matches + listings + profiles fan-out) and even
    // writes back to the listened doc (cleanup), so identical re-deliveries
    // must be skipped before the pipeline starts. Pattern from the iOS listen
    // storm caught on the offer page (same doc delivered every ~8ms).
    let lastUserSnapVersion = null;
    const unsubscribe = onSnapshot(doc(db, 'users', currentUser.uid), async (userSnap) => {
      const snapVersion = userSnap.updateTime ? userSnap.updateTime.toMillis() : null;
      if (snapVersion !== null && lastUserSnapVersion === snapVersion && !userSnap.metadata.hasPendingWrites) {
        return;
      }
      lastUserSnapVersion = snapVersion;
      try {
      const legacyIds = userSnap.exists() ? userSnap.data().savedMatches || [] : [];
      const savedUids = userSnap.data()?.savedMatchUsers || [];
      setHiddenMatchUsers(userSnap.data()?.hiddenMatchUsers || []);

      // Validate saved matches against the matches collection — prune stale entries
      const [asMyPedalSnap, asMatchedPedalSnap] = await Promise.all([
        getDocs(query(collection(db, 'matches'), where('myPedalUser', '==', currentUser.uid))),
        getDocs(query(collection(db, 'matches'), where('matchedPedalUser', '==', currentUser.uid)))
      ]);
      // Build a per-doc index keyed on the Firestore doc id (same composite-id
      // form used in the saved-matches array). Carries createdAt + counterparty
      // uid so Phase 4 hydration can attach those without a second round-trip.
      const matchDocIndex = new Map();
      asMyPedalSnap.docs.forEach(d => {
        const data = d.data();
        matchDocIndex.set(d.id, { createdAt: data.createdAt, counterpartyUid: data.matchedPedalUser });
      });
      asMatchedPedalSnap.docs.forEach(d => {
        const data = d.data();
        // Perspective swap: when current user is the matchedPedalUser side,
        // the counterparty is the myPedalUser side.
        matchDocIndex.set(d.id, { createdAt: data.createdAt, counterpartyUid: data.myPedalUser });
      });
      const validMatchDocIds = new Set(matchDocIndex.keys());

      // Resolve every matchId implied by savedMatchUsers (counterparty UIDs)
      // by walking matchDocIndex and picking out docs whose counterparty
      // matches one of the saved UIDs. These join the legacyIds set, deduped.
      const savedUidSet = new Set(savedUids);
      const fromSavedUsers = [];
      for (const [docId, info] of matchDocIndex.entries()) {
        if (savedUidSet.has(info.counterpartyUid)) fromSavedUsers.push(docId);
      }
      const savedMatchIds = [...new Set([...legacyIds, ...fromSavedUsers])];

      // Fetch ONLY the listing docs referenced by these matches, instead of the
      // entire listings collection. A match id is `myPedalId_theirPedalId`, and
      // listing doc ids ARE pedal ids — so we can read them directly by id and
      // reuse the result for both orphan cleanup and card hydration below.
      const referencedPedalIds = [...new Set(savedMatchIds.flatMap(id => id.split('_')))];
      const listingSnaps = await Promise.all(
        referencedPedalIds.map(pid => getDoc(doc(db, 'listings', pid)))
      );
      const listingsById = new Map();
      listingSnaps.forEach((snap, i) => {
        if (snap.exists()) listingsById.set(referencedPedalIds[i], { id: snap.id, ...snap.data() });
      });

      // Clean up any orphaned saved matches first (uses the fetched listings)
      await cleanupOrphanedMatches(savedMatchIds, listingsById);

      const staleIds = savedMatchIds.filter(compositeId => {
        const [idA, idB] = compositeId.split('_');
        const matchDocId = idA < idB ? `${idA}_${idB}` : `${idB}_${idA}`;
        return !validMatchDocIds.has(matchDocId);
      });
      let validSavedMatchIds = savedMatchIds;
      if (staleIds.length > 0) {
        validSavedMatchIds = savedMatchIds.filter(id => !staleIds.includes(id));
        const userRef = doc(db, 'users', currentUser.uid);
        await updateDoc(userRef, { savedMatches: validSavedMatchIds });
      }

      // Suppress saved cards for any counterparty currently in hiddenMatchUsers
      // — see handleGroupHide for the symmetric write-side behavior. Without
      // this filter, the saved card would still render for the brief tick
      // between Hide click and Firestore confirmation, AND for any prior
      // legacy state where a counterparty was both saved and hidden.
      const hiddenUidSet = new Set(
        (userSnap.data()?.hiddenMatchUsers || []).map(h => h.counterpartyUid)
      );
      validSavedMatchIds = validSavedMatchIds.filter(id => {
        const info = matchDocIndex.get(id);
        return !info || !hiddenUidSet.has(info.counterpartyUid);
      });

      // Get user's own trade offers to check for completed trades
      const userTradeOffersQuery = query(
        collection(db, 'tradeOffers'),
        where('fromUserId', '==', currentUser.uid)
      );
      const userTradeOffersSnap = await getDocs(userTradeOffersQuery);
      const userTradeOffers = userTradeOffersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Also get trade offers where user is the responder
      const responderTradeOffersQuery = query(
        collection(db, 'tradeOffers'),
        where('toUserId', '==', currentUser.uid)
      );
      const responderTradeOffersSnap = await getDocs(responderTradeOffersQuery);
      const responderTradeOffers = responderTradeOffersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Combine all user's trade offers
      const allUserTrades = [...userTradeOffers, ...responderTradeOffers];
      const completedTrades = allUserTrades.filter(trade => trade.status === 'completed');

      // Per-run cache: counterparties repeat across a user's matches (cards are
      // grouped by user), so dedupe publicProfiles reads instead of fetching the
      // same profile once per match.
      const profileCache = new Map();
      const getProfile = async (uid) => {
        if (!uid) return {};
        if (profileCache.has(uid)) return profileCache.get(uid);
        const snap = await getDoc(doc(db, 'publicProfiles', uid));
        const data = snap.exists() ? snap.data() : {};
        profileCache.set(uid, data);
        return data;
      };

      const matchedPairs = await Promise.all(
        validSavedMatchIds.map(async (id) => {
          const [myId, theirId] = id.split('_');
          const myPedal = listingsById.get(myId);
          const matchedPedal = listingsById.get(theirId);

          // Check if pedals exist and are available for matching
          if (!myPedal || !matchedPedal) return null;
          if (myPedal.active === false || matchedPedal.active === false) return null;
          if (myPedal.inTrade === true || matchedPedal.inTrade === true) return null;
          if (myPedal.needsReactivation === true || matchedPedal.needsReactivation === true) return null;

          // Skip match document check - we reconstruct matches from pedal IDs

          // Check if either pedal has been traded (completed) — multi-pedal aware
          const isMyPedalTraded = completedTrades.some(trade => 
            getInitiatorPedalIds(trade).includes(myId) || trade.responderPedalId === myId
          );
          const isMatchedPedalTraded = completedTrades.some(trade => 
            getInitiatorPedalIds(trade).includes(theirId) || trade.responderPedalId === theirId
          );

          if (isMyPedalTraded || isMatchedPedalTraded) return null;

          // Perspective swap: Firestore doc IDs are canonical (sorted pedal IDs),
          // so the viewer's pedal may be in either slot. Ensure myPedal is always
          // the viewer's and matchedPedal is always the counterparty's — the same
          // swap Matches.jsx does on the matchedPedalUser side before enrichment.
          const needsSwap = myPedal.ownerId !== currentUser.uid;
          const effectiveMyPedal    = needsSwap ? matchedPedal : myPedal;
          const effectiveTheirPedal = needsSwap ? myPedal      : matchedPedal;
          const myOwnerId    = effectiveMyPedal.ownerId    || currentUser.uid;
          const theirOwnerId = effectiveTheirPedal.ownerId;

          // PII isolation: counterparty cards read from public mirror (cached).
          const [myData, theirData] = await Promise.all([
            getProfile(myOwnerId),
            getProfile(theirOwnerId)
          ]);

          // No need to check for banned users - Cloud Functions handle this automatically

          const matchInfo = matchDocIndex.get(id);
          return {
            matchId: id,
            counterpartyUid: matchInfo?.counterpartyUid || theirOwnerId,
            createdAt: matchInfo?.createdAt,
            myPedal:      effectiveMyPedal,
            matchedPedal: effectiveTheirPedal,
            myPedalUser: {
              username: myData.username || 'User',
              badge: getTradeBadge(myData.completedTrades || 0),
              membershipTier: myData.membershipTier || 'free',
              photoURL: myData.photoURL || null,
              averageRating: myData.averageRating || 0
            },
            matchedPedalUser: {
              username: theirData.username || 'User',
              badge: getTradeBadge(theirData.completedTrades || 0),
              membershipTier: theirData.membershipTier || 'free',
              photoURL: theirData.photoURL || null,
              averageRating: theirData.averageRating || 0
            }
          };
        })
      );

      const filtered = matchedPairs.filter(pair => pair !== null);
      // Per-match flat sort removed — promotion sort (18V counterparty first) happens
      // at the group level inside groupMatchesByUser(), checking only the counterparty's
      // membershipTier, not the viewing user's. Mirrors what Matches.jsx does.
      setMatches(filtered);
      } finally {
        setMatchesLoading(false);
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Filter pipeline lives in useMatchFilters (see hook call near top of
  // component). The hook publishes `filteredMatches` which the userGroups
  // useMemo below consumes.

  const getMatchId = (match) => {
    return `${match.myPedal?.id}_${match.matchedPedal?.id}`;
  };

  // ── Group-card derivation ──────────────────────────────────────────────
  // The existing inline filter pipeline writes filteredMatches; we group those
  // for the new card UI. passingMatchIds is just every matchId in the filtered
  // set — this page has no separate "visible vs passing" distinction (unlike
  // Matches.jsx, where visible includes hidden-pruned and passing reflects the
  // content filter).
  const userGroups = useMemo(() => {
    if (filteredMatches.length === 0) return [];
    const passingMatchIds = new Set(filteredMatches.map(m => m.matchId));
    return groupMatchesByUser(filteredMatches, passingMatchIds);
  }, [filteredMatches]);

  return (
    <>
      <style>{`
        @media (min-width: 769px) {
          .saved-matches-main-container {
            max-width: 1100px !important;
            padding: 0 2rem !important;
            box-sizing: border-box !important;
          }
        }
        @media (max-width: 768px) {
          body { overflow-x: hidden !important; }
          .saved-matches-page {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .saved-matches-main-container {
            display: block !important;
            width: auto !important;
            margin: 0 0.5rem 0 0.5rem !important;
            max-width: none !important;
            min-width: auto !important;
            padding: 0 !important;
          }
        }
      `}</style>
      <div {...swipeProps} className="saved-matches-page" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))' }}>
      <div className="matches-page-content" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
      <MatchesMobileChromeHeader activePath={location.pathname} filters={filters}>
        <Link
          to="/matches"
          className="matches-header-back-link"
          style={{
            color: '#5A9ECB',
            alignItems: 'center',
            marginTop: '2rem',
            marginBottom: '1rem',
            fontSize: '1.125rem',
            textDecoration: 'none',
          }}
          onMouseEnter={(e) => { e.target.style.textDecoration = 'underline'; }}
          onMouseLeave={(e) => { e.target.style.textDecoration = 'none'; }}
        >
          ← Back to Matches
        </Link>
        <div className="matches-header-expanded-inner" style={{ marginBottom: '2rem' }}>
          <div className="matches-header-copy">
            <h1 className="font-bold text-gray-900" style={{ fontSize: '2.25rem', fontWeight: '700', letterSpacing: '-0.025em' }}>
              Saved Matches
            </h1>
            <p className="matches-header-description text-gray-600" style={{ fontSize: '17px' }}>
              These are matches you&apos;ve saved for later review.
            </p>
          </div>
        </div>
        <div className="matches-desktop-filter-bar">
          <MatchFilterBar filters={filters} />
        </div>
      </MatchesMobileChromeHeader>

      <div className="swipe-clip">
      <div className={['swipe-pane', viewClass].filter(Boolean).join(' ')}>
      <div className="saved-matches-main-container" style={{ maxWidth: '1100px', width: '100%', margin: '2rem auto 0 auto', padding: '0 2rem', boxSizing: 'border-box' }}>
        {authLoading || matchesLoading ? (
          <div className="text-center text-gray-500 italic mt-8" style={{ width: '100%' }}>
            Loading saved matches...
          </div>
        ) : userGroups.length === 0 ? (
          <div className='text-center text-gray-500 italic mt-8' style={{ width: '100%' }}>
            {activeFilterCount > 0
              ? 'No matches found with this criteria. Try adjusting your filters.'
              : "You haven't saved any matches yet."}
          </div>
        ) : (
          userGroups.map((group) => (
            <UserGroupCard
              key={group.counterpartyUid}
              group={group}
              isSaved={true}
              primaryButton={{
                label: 'Saved',
                onClick: () => handleGroupUnsave(group)
              }}
              secondaryButton={{
                label: 'Hide Match',
                onClick: () => handleGroupHide(group, hiddenMatchUsers)
              }}
            />
          ))
        )}
      </div>

      {/* Fixed gap below content */}
      <div style={{ height: '60px' }}></div>
      
      {/* Footer */}
      <div className="bg-gray-100 border-t border-gray-200 py-4" style={{ paddingBottom: '10px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Need help or found a bug?{' '}
              <button 
                onClick={() => openContact({ category: 'support' })}
                className="hover:underline"
                style={{ 
                  color: '#5A9ECB',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer'
                }}
              >
                Contact PXC Support
              </button>
            </p>
          </div>
        </div>
      </div>
      </div>
      </div>
      </div>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="back-to-top-btn"
          style={{
            position: 'fixed',
            bottom: window.innerWidth <= 768 ? '90px' : '30px',
            right: '30px',
            backgroundColor: '#5A9ECB',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#4A8EBB';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#5A9ECB';
            e.target.style.transform = 'scale(1)';
          }}
          aria-label="Back to top"
        >
          ⌃
        </button>
      )}
    </>
  );
};

export default SavedMatches;
